import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { useSnackPackContext } from "@app.automotus.io/components/context/SnackPack";
import { useUserProfile } from "@app.automotus.io/components/hooks";
import {
  SUBMIT_PERMIT_APPLICATION,
  SubmitPermitApplicationData,
  SubmitPermitApplicationVars,
  PermitAttachedDocument,
} from "common/graphql/mutations/SubmitPermitApplication";
import { usePermitApplicationContext } from "../PermitApplicationContext";

export interface UsePermitSubmissionResult {
  /** Whether the submission is in progress */
  isSubmitting: boolean;
  /** Submits the permit application */
  submitApplication: () => Promise<void>;
}

/**
 * Hook that manages permit application submission.
 *
 * Handles:
 * - Calling the submitPermitApplication mutation with data from context
 * - Showing success/error snackbar notifications
 * - Resetting application state on success
 * - Navigating to /permits on success
 */
export function usePermitSubmission(): UsePermitSubmissionResult {
  const navigate = useNavigate();
  const { publishSnackbarMessage } = useSnackPackContext();
  const { userProfile } = useUserProfile();
  const { state, resetApplication } = usePermitApplicationContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [submitMutation] = useMutation<SubmitPermitApplicationData, SubmitPermitApplicationVars>(
    SUBMIT_PERMIT_APPLICATION,
  );

  const submitApplication = useCallback(async () => {
    const { permitTypeSelection, vehicleSelection, documents, acceptedTermsAt } = state;

    // Validate required state
    if (!permitTypeSelection.permitTypeId) {
      publishSnackbarMessage({
        severity: "error",
        message: "Please select a permit type before submitting.",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      return;
    }

    if (!acceptedTermsAt) {
      publishSnackbarMessage({
        severity: "error",
        message: "Please accept the terms and conditions before submitting.",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      return;
    }

    if (!userProfile?.account?.id) {
      publishSnackbarMessage({
        severity: "error",
        message: "Unable to determine account. Please refresh and try again.",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare attached documents - only include those that have been uploaded
      const attachedDocuments: PermitAttachedDocument[] = documents
        .filter((doc) => doc.documentId !== null)
        .map((doc) => ({
          document_type_id: doc.documentTypeId,
          document_id: doc.documentId as string,
        }));

      // Prepare vehicle IDs - use selected vehicles or empty array for per_account scope
      // TODO: this will not work with account-wide selection
      const assignedVehicleIds = [...vehicleSelection.selectedVehicleIds];

      await submitMutation({
        variables: {
          permitTypeId: permitTypeSelection.permitTypeId,
          assignedVehicleIds,
          attachedDocuments,
          termsAndConditionsAcceptedAt: acceptedTermsAt,
        },
      });

      publishSnackbarMessage({
        severity: "success",
        message: "Application submitted successfully.",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });

      resetApplication();
      navigate("/permits");
    } catch (error) {
      console.error("Failed to submit permit application:", error);
      publishSnackbarMessage({
        severity: "error",
        message: "Failed to submit application. Please try again.",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [state, userProfile, submitMutation, publishSnackbarMessage, resetApplication, navigate]);

  return {
    isSubmitting,
    submitApplication,
  };
}

export default usePermitSubmission;
